import { Fragment, useEffect, useState } from "react";
import classes from "../homePageNew.module.css";
import CombinationSelector from "./CombinationSelector";
import LlmScoreTable from "./LlmScoreTable";
import ReportStrip from "./ReportStrip";
import FailedRunPanel from "./FailedRunPanel";
import EmptyRecordState from "./EmptyRecordState";
import SectionEmptyState from "./SectionEmptyState";
import ShareModal from "./ShareModal";
import { CategoryIcon, ShareIcon, ChevronDownIcon } from "./icons";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import { rbiWeightLabel } from "../../../utils/brandIndexCalc";

import { pillClass, findLeaderboardEntry, findAllLeaderboardEntries, llmLabel } from "../api/homePageApi";

const DIMENSIONS_SINGLE = [
  { key: "category", label: "Category" },
  { key: "market", label: "Market" },
  { key: "persona", label: "Persona" },
  { key: "llm", label: "LLM", format: llmLabel },
];
const DIMENSIONS_COMPOSITE = [
  { key: "category", label: "Category" },
  { key: "market", label: "Market" },
  { key: "persona", label: "Persona" },
];

function fmt(value, decimals) {
  if (value == null) return "—";
  return Number(value).toFixed(decimals);
}

function MetricHead({ color, label, weight }) {
  return (
    <span className={classes.caLbHeadMetric}>
      <span className={classes.caLbHeadDot} style={{ background: color }} />
      {label} {weight && <span className={classes.caLbHeadWeight}>{weight}</span>}
    </span>
  );
}

// Which 4 metrics the RBI column/percentages depend on — same set the real
// Category Analysis dashboard treats as "all metrics selected"
// (CategoryScoreCardsNew.js's RBI_REQUIRED_METRICS).
const RBI_REQUIRED_METRICS = ["rank", "sentiment", "coreAttribute", "categoryDimension"];

// Column defs for the leaderboard table — one entry per possible metric,
// filtered down to whichever ones this run was actually launched with.
// `decimals`/`compositeDecimals` differ per metric: Single View's Visibility
// and Sentiment are each one real LLM's own raw score, so whole numbers;
// By LLM (Compare All)'s numbers are an average across several LLMs, so
// Sentiment/Pillars/P.Crit keep one decimal there to show the averaging —
// Visibility stays a whole number in both views per explicit request.
const LEADERBOARD_METRIC_COLUMNS = [
  { metricKey: "rank", scoreKey: "visibility_score", label: "Visibility", get weight() { return rbiWeightLabel("visibility"); }, color: "var(--hpn-d-blue)", suffix: "/50", decimals: 0, compositeDecimals: 0 },
  { metricKey: "sentiment", scoreKey: "sentiment_score", label: "Sentiment", get weight() { return rbiWeightLabel("sentiment"); }, color: "var(--hpn-d-orange)", suffix: "/50", decimals: 0, compositeDecimals: 1 },
  { metricKey: "coreAttribute", scoreKey: "brand_pillars_score", label: "Pillars", get weight() { return rbiWeightLabel("brandPillars"); }, color: "var(--hpn-d-purple)", suffix: "/10", decimals: 1, compositeDecimals: 1 },
  { metricKey: "categoryDimension", scoreKey: "purchase_criteria_score", label: "P.Crit", get weight() { return rbiWeightLabel("purchaseCriteria"); }, color: "var(--hpn-d-green)", suffix: "/10", decimals: 1, compositeDecimals: 1 },
];

/**
 * Category Analysis's own detail pane — fundamentally different shape from
 * every other module here: there's no main brand, a record is a whole
 * competitive leaderboard (several competitor brands) for a category, so
 * this renders a brand-comparison table instead of one brand's donut +
 * metrics. Reuses CombinationSelector (already dimension-driven — LLM is
 * just a 4th dimension here, unlike Brand Audit which excludes it) and
 * LlmScoreTable (for each brand's per-LLM breakdown row) rather than
 * building new pickers/tables from scratch.
 *
 * Two data sources, one per view mode, both read from record.metrics[]:
 *   Single View → the one `kind: "leaderboard"` entry matching the full
 *                 category/market/persona/llm selection.
 *   By LLM      → the one `kind: "leaderboard_composite"` entry (llm: "ALL")
 *                 BE pre-computes once 2+ LLMs ran that category/market/
 *                 persona — LLM is dropped from the selector in this mode
 *                 since there's nothing to pick, the table already merges
 *                 every model.
 * Clicking a brand row expands it in place (only one open at a time) to
 * show that brand's own per-LLM numbers — sourced from every `leaderboard`
 * entry for the current category/market/persona, independent of which
 * view mode or which single LLM is selected, since that breakdown is
 * useful context either way.
 */
export default function CategoryAnalysisDetailPane({
  record,
  moduleLabel,
  noDataAtAll,
  moduleKey,
  combination,
  onCombinationChange,
  onRetried,
  onViewFull,
  fullViewUrl,
  userId,
  onOpenReportsModal,
  onViewReport,
}) {
  const [shareOpen, setShareOpen] = useState(false);
  const [viewMode, setViewMode] = useState("single");
  const [expandedBrand, setExpandedBrand] = useState(null);

  useEffect(() => {
    setViewMode("single");
    setExpandedBrand(null);
  }, [record?.transactionId]);

  useEffect(() => {
    setExpandedBrand(null);
  }, [viewMode]);

  if (!record) {
    return (
      <div className={classes.mdDetailPane}>
        <EmptyRecordState moduleLabel={moduleLabel} noDataAtAll={noDataAtAll} />
      </div>
    );
  }

  const status = pillClass(record.status);

  if (status === "failed") {
    return (
      <div className={classes.mdDetailPane}>
        <FailedRunPanel transactionId={record.transactionId} failReason={record.failReason} onRetried={onRetried} />
      </div>
    );
  }

  const entries = Array.isArray(record.metrics) ? record.metrics : [];
  const leaderboardEntries = entries.filter((e) => e?.kind === "leaderboard");
  const combinations = leaderboardEntries.map((e) => e.combination).filter(Boolean);
  const dataUnavailable = leaderboardEntries.length === 0;

  const effective = {
    category: combination?.category ?? combinations[0]?.category ?? null,
    market: combination?.market ?? combinations[0]?.market ?? null,
    persona: combination?.persona ?? combinations[0]?.persona ?? null,
    llm: combination?.llm ?? combinations[0]?.llm ?? null,
  };

  const scopeCombination = { category: effective.category, market: effective.market, persona: effective.persona };
  const activeEntry =
    viewMode === "llm"
      ? findLeaderboardEntry(record, scopeCombination, "leaderboard_composite")
      : findLeaderboardEntry(record, effective, "leaderboard");
  // Highest brand_index_score first — a competitive leaderboard reads
  // naturally sorted, top performer first, in both view modes.
  const rows = [...(activeEntry?.leaderboard || [])].sort(
    (a, b) => (b.scores?.brand_index_score ?? -Infinity) - (a.scores?.brand_index_score ?? -Infinity)
  );
  const compositeUnavailable = viewMode === "llm" && !dataUnavailable && !activeEntry;
  const llmCountForScope = findAllLeaderboardEntries(record, scopeCombination, "leaderboard").length;

  const competitorList = record.competitor?.[effective.category] || [];
  const domainByBrand = Object.fromEntries(competitorList.map((c) => [c.name, c.domain]));

  // This run's launch-time metric selection — missing/empty (older runs,
  // or BE not sending it yet) defaults to "all 4", i.e. today's unchanged
  // behavior. Drives both the leaderboard table's own columns below and
  // the per-LLM breakdown table (LlmScoreTable) it expands into.
  const selectedMetrics = record.selectedMetrics?.length > 0 ? record.selectedMetrics : RBI_REQUIRED_METRICS;
  const allRbiMetricsSelected = RBI_REQUIRED_METRICS.every((m) => selectedMetrics.includes(m));
  const visibleMetricColumns = LEADERBOARD_METRIC_COLUMNS.filter((c) => selectedMetrics.includes(c.metricKey));
  const expandColSpan = 1 + visibleMetricColumns.length + (allRbiMetricsSelected ? 1 : 0);

  function buildLlmRowsFor(brandName) {
    return findAllLeaderboardEntries(record, scopeCombination, "leaderboard")
      .map((e) => {
        const row = (e.leaderboard || []).find((r) => r.brand === brandName);
        return row ? { llm: e.combination?.llm, scores: row.scores } : null;
      })
      .filter(Boolean);
  }

  const label = `${effective.category || "Category"} Category Analysis`;

  const headerSubtitleParts = [
    effective.market,
    effective.persona,
    viewMode === "llm" ? "All LLMs" : llmLabel(effective.llm),
  ].filter(Boolean);

  return (
    <div className={classes.mdDetailPane}>
      <div className={classes.detailHeader}>
        <div className={classes.detailHeaderText}>
          <div className={classes.detailHeaderName}>{effective.category || "Category Analysis"}</div>
          {headerSubtitleParts.length > 0 && (
            <div className={classes.detailHeaderSubtitle}>{headerSubtitleParts.join(" · ")}</div>
          )}
        </div>
      </div>

      {dataUnavailable ? (
        <SectionEmptyState
          icon={CategoryIcon}
          title="Full scores aren't available yet"
          description="This run hasn't finished processing — the competitive leaderboard will show here once it's ready."
        />
      ) : (
        <>
          <div className={classes.caToggleRow}>
            <div className={classes.caViewToggle}>
              <button
                type="button"
                className={`${classes.caViewToggleBtn} ${viewMode === "single" ? classes.caViewToggleBtnActive : ""}`}
                onClick={() => setViewMode("single")}
              >
                Single View
              </button>
              <button
                type="button"
                className={`${classes.caViewToggleBtn} ${viewMode === "llm" ? classes.caViewToggleBtnActive : ""}`}
                onClick={() => setViewMode("llm")}
              >
                By LLM (Compare All)
              </button>
            </div>
            <span className={classes.caToggleMeta}>
              {viewMode === "llm"
                ? `Comparing across ${llmCountForScope} LLM${llmCountForScope !== 1 ? "s" : ""} · click any row to expand breakdown`
                : `Showing ${llmLabel(effective.llm) || "—"} only`}
            </span>
          </div>

          <CombinationSelector
            combinations={combinations}
            value={effective}
            onChange={onCombinationChange}
            dimensions={viewMode === "llm" ? DIMENSIONS_COMPOSITE : DIMENSIONS_SINGLE}
          />

          {compositeUnavailable ? (
            <SectionEmptyState
              icon={CategoryIcon}
              title="Composite score isn't available yet"
              description="The merged By-LLM average for this category/market/persona hasn't been computed yet."
            />
          ) : rows.length === 0 ? (
            <SectionEmptyState
              icon={CategoryIcon}
              title="No competitor data for this combination"
              description="Try a different category, market, or persona above."
            />
          ) : (
            <div className={classes.caLbWrap}>
              <div className={classes.caLbScroll}>
                <table className={classes.caLbTable}>
                  <thead>
                    <tr>
                      <th>Brand</th>
                      {visibleMetricColumns.map((c) => (
                        <th key={c.metricKey}>
                          <MetricHead color={c.color} label={c.label} weight={allRbiMetricsSelected ? c.weight : null} />
                        </th>
                      ))}
                      {allRbiMetricsSelected && <th>RBI</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isOpen = viewMode === "llm" && expandedBrand === row.brand;
                      return (
                        <Fragment key={row.brand}>
                          <tr
                            className={`${classes.caLbRow} ${viewMode === "llm" ? classes.caLbRowInteractive : ""} ${isOpen ? classes.caLbRowOpen : ""}`}
                            onClick={viewMode === "llm" ? () => setExpandedBrand(isOpen ? null : row.brand) : undefined}
                          >
                            <td>
                              <div className={classes.caLbBrandCell}>
                                <BrandAvatar domainName={domainByBrand[row.brand]} brandName={row.brand} />
                                <span>{row.brand}</span>
                                {/* Single View already shows exactly one combination's numbers — nothing
                                    further to reveal, so no chevron there; only By LLM's merged row has a
                                    per-model breakdown to expand. */}
                                {viewMode === "llm" && (
                                  <span className={`${classes.caLbChevron} ${isOpen ? classes.caLbChevronOpen : ""}`}>
                                    <ChevronDownIcon size={13} />
                                  </span>
                                )}
                              </div>
                            </td>
                            {visibleMetricColumns.map((c) => (
                              <td key={c.metricKey}>
                                {fmt(row.scores?.[c.scoreKey], viewMode === "llm" ? c.compositeDecimals : c.decimals)}
                                {c.suffix}
                              </td>
                            ))}
                            {allRbiMetricsSelected && (
                              <td className={classes.caLbScoreStrong}>{fmt(row.scores?.brand_index_score, 0)}</td>
                            )}
                          </tr>
                          {isOpen && (
                            <tr className={classes.caLbExpandRow}>
                              <td colSpan={expandColSpan}>
                                <div className={classes.caLbExpandInner}>
                                  <LlmScoreTable
                                    rows={buildLlmRowsFor(row.brand)}
                                    primaryLLM={null}
                                    isProduct={false}
                                    visibleMetrics={allRbiMetricsSelected ? null : selectedMetrics}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <div className={classes.detailActionsRow}>
        <button
          className={classes.viewBtn}
          onClick={() => onViewFull(record)}
          disabled={dataUnavailable && status !== "inProgress"}
          title={dataUnavailable && status !== "inProgress" ? "Full detail isn't available yet for this run" : undefined}
        >
          View {moduleLabel} →
        </button>
        {fullViewUrl && (
          <button
            className={classes.drawerActionBtn}
            onClick={() => setShareOpen(true)}
            disabled={status === "inProgress"}
            title={status === "inProgress" ? "Sharing isn't available until the run finishes" : "Share"}
          >
            <ShareIcon size={14} />
            Share
          </button>
        )}
      </div>
      <ReportStrip
        report={record.report}
        unavailable={dataUnavailable}
        onOpenReportsModal={() => onOpenReportsModal(record)}
        onViewReport={onViewReport}
        moduleKey={moduleKey}
      />
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        itemName={label}
        documentPath={fullViewUrl ? `${window.location.origin}${fullViewUrl}` : ""}
        transactionId={record.transactionId}
        userId={userId}
      />
    </div>
  );
}
